<script setup>
/**
 * PWA Install Prompt Modal
 * Shows a branded install prompt for Android/Desktop (native install)
 * and step-by-step guide for iOS (Add to Home Screen).
 */
import { useInstallPrompt } from '@core/composables/useInstallPrompt.js'

const { isVisible, platform, dismiss, triggerInstall } = useInstallPrompt()
</script>

<template>
  <Teleport to="body">
    <Transition name="install-modal">
      <div v-if="isVisible" class="install-overlay" @click.self="dismiss">
        <div class="install-modal">
          <!-- Close button -->
          <button class="install-close" @click="dismiss">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <!-- App icon -->
          <div class="install-icon-wrap">
            <img src="/pwa-192x192.png" alt="GoFood" width="80" height="80" class="install-icon" />
          </div>

          <!-- Title -->
          <div class="install-content">
            <h2 class="install-title">GoFood</h2>
            <p class="install-subtitle">גישה מהירה להזמנות, מבצעים ועדכונים — ישירות מהמסך הראשי</p>
          </div>

          <!-- Android / Desktop Chromium -->
          <div
            v-if="platform === 'android' || platform === 'desktop-chromium'"
            class="install-actions"
          >
            <button class="install-primary-btn" @click="triggerInstall">
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              התקן אפליקציה
            </button>
            <button class="install-dismiss-link" @click="dismiss">לא עכשיו</button>
          </div>

          <!-- iOS -->
          <div v-else-if="platform === 'ios'" class="install-ios">
            <div class="install-steps">
              <!-- Step 1: Share -->
              <div class="install-step">
                <span class="install-step-num">1</span>
                <div class="install-step-icon">
                  <svg
                    viewBox="0 0 24 24"
                    width="22"
                    height="22"
                    fill="none"
                    stroke="var(--color-info)"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                </div>
                <span class="install-step-text">לחצו על כפתור השיתוף</span>
              </div>

              <!-- Step 2: Add to Home Screen -->
              <div class="install-step">
                <span class="install-step-num">2</span>
                <div class="install-step-icon">
                  <svg
                    viewBox="0 0 24 24"
                    width="22"
                    height="22"
                    fill="none"
                    stroke="var(--color-info)"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <line x1="12" y1="8" x2="12" y2="16" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                  </svg>
                </div>
                <span class="install-step-text">בחרו &#x27;הוסף למסך הבית&#x27;</span>
              </div>

              <!-- Step 3: Confirm -->
              <div class="install-step">
                <span class="install-step-num">3</span>
                <div class="install-step-icon">
                  <svg
                    viewBox="0 0 24 24"
                    width="22"
                    height="22"
                    fill="none"
                    stroke="var(--color-success)"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <span class="install-step-text">לחצו &#x27;הוסף&#x27; בפינה העליונה</span>
              </div>
            </div>

            <button class="install-primary-btn" @click="dismiss">הבנתי, תודה!</button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.install-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9998;
  padding: var(--spacing-md);
}

.install-modal {
  position: relative;
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl) var(--spacing-lg) var(--spacing-lg);
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: var(--shadow-xl);
}

/* Close button */
.install-close {
  position: absolute;
  top: var(--spacing-sm);
  left: var(--spacing-sm);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.install-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

/* App icon */
.install-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-lg);
}

.install-icon {
  width: 80px;
  height: 80px;
  border-radius: 18px;
  box-shadow: 0 4px 16px rgba(30, 41, 59, 0.15);
}

/* Content */
.install-content {
  margin-bottom: var(--spacing-lg);
}

.install-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs);
}

.install-subtitle {
  font-size: var(--font-size-base);
  line-height: 1.6;
  color: var(--text-secondary);
  margin: 0;
}

/* Actions (Android/Desktop) */
.install-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
}

.install-primary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-xl);
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.install-primary-btn:hover {
  background: var(--color-primary-hover);
  box-shadow: 0 4px 12px rgba(30, 41, 59, 0.3);
}

.install-primary-btn:active {
  transform: scale(0.98);
}

.install-dismiss-link {
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
  padding: var(--spacing-xs) var(--spacing-sm);
  transition: color var(--transition-fast);
}

.install-dismiss-link:hover {
  color: var(--text-secondary);
  text-decoration: underline;
}

/* iOS steps */
.install-ios {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.install-steps {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.install-step {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
  text-align: right;
}

.install-step-num {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary);
  color: white;
  border-radius: 50%;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
}

.install-step-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.install-step-text {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
}

/* Transitions */
.install-modal-enter-active,
.install-modal-leave-active {
  transition: opacity 0.25s ease;
}

.install-modal-enter-active .install-modal,
.install-modal-leave-active .install-modal {
  transition: transform 0.25s ease;
}

.install-modal-enter-from,
.install-modal-leave-to {
  opacity: 0;
}

.install-modal-enter-from .install-modal,
.install-modal-leave-to .install-modal {
  transform: scale(0.95);
}

/* Mobile */
@media (max-width: 480px) {
  .install-overlay {
    padding: var(--spacing-sm);
    align-items: flex-end;
  }

  .install-modal {
    max-width: 100%;
    border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
    padding-bottom: calc(var(--spacing-lg) + env(safe-area-inset-bottom, 0px));
  }
}
</style>
